import { nanoid } from 'nanoid';
import { parseAsArrayOf, parseAsJson } from 'nuqs';
import { z } from 'zod';

import { ESignatureRequestStatus } from '@/typings/api-models';

export const queryParam = 'panes';

const baseSchema = z.object({
  id: z.string(),
  key: z.string(),
});

const initiateCaseSchema = baseSchema.extend({
  associatedUserWorkflowId: z.string().optional(),
  customer: z
    .object({
      email: z.string().email().nullable(),
      id: z.string(),
      name: z.string().nullable(),
    })
    .optional(),
  owners: z
    .array(
      z.object({
        personId: z.string().uuid(),
        workflowRoles: z.array(z.string()).optional(),
      }),
    )
    .optional(),
  type: z.literal('initiateCase'),
});

const activityThreadSchema = baseSchema.extend({
  type: z.literal('activityThread'),
});

const userWorkflowDocumentSchema = baseSchema.extend({
  type: z.literal('userWorkflowDocument'),
});

const aiFollowupsSchema = baseSchema.extend({
  type: z.literal('aiFollowups'),
});

const allCasesSchema = baseSchema.extend({
  type: z.literal('allCases'),
});

const creditReportSchema = baseSchema.extend({
  type: z.literal('creditReport'),
});

const customTermsSchema = baseSchema.extend({
  type: z.literal('customTerms'),
});

const dataReferenceSchema = baseSchema.extend({
  type: z.literal('dataReference'),
});

const documentRequestUserSchema = baseSchema.extend({
  type: z.literal('documentRequestUser'),
});

const documentRequestUserFileSchema = baseSchema.extend({
  type: z.literal('documentRequestUserFile'),
});

const followupHistorySchema = baseSchema.extend({
  type: z.literal('followupHistory'),
});

const formRequestSchema = baseSchema.extend({
  type: z.literal('formRequest'),
});

const invoiceSchema = baseSchema.extend({
  type: z.literal('invoice'),
});

const previewDocumentSchema = baseSchema.extend({
  type: z.literal('previewDocument'),
});

const formRequestTemplateSchema = baseSchema.extend({
  formRequestTemplateId: z.string().optional(),
  type: z.literal('formRequestTemplate'),
});

const workflowTemplateSchema = baseSchema.extend({
  type: z.literal('workflowTemplate'),
});

const userWorkflowDocumentsSchema = baseSchema.extend({
  type: z.literal('userWorkflowDocuments'),
});

const helpCenterSchema = baseSchema.extend({
  type: z.literal('helpCenter'),
});

const submissionAttemptSchema = baseSchema.extend({
  type: z.literal('submissionAttempt'),
});

const pacerPreviewSchema = baseSchema.extend({
  type: z.literal('pacerPreview'),
});

const governmentFormSchema = baseSchema.extend({
  type: z.literal('governmentForm'),
});

const bankStatementOrganizerSchema = baseSchema.extend({
  type: z.literal('bankStatementOrganizer'),
});

const ESignatureRequestSchema = baseSchema.extend({
  requestStatus: z.nativeEnum(ESignatureRequestStatus).optional(),
  type: z.literal('eSignatureRequest'),
});

const anvilCastEditorSchema = baseSchema.extend({
  creatorId: z.string(),
  embedUrl: z.string(),
  formRequestTemplateId: z.string().optional(),
  formRequestTemplateSlug: z.string().optional(),
  mode: z.enum(['create', 'edit']).default('create'),
  title: z.string(),
  type: z.literal('anvilCastEditor'),
});

const queryStateSchema = z.discriminatedUnion('type', [
  anvilCastEditorSchema,
  initiateCaseSchema,
  activityThreadSchema,
  userWorkflowDocumentSchema,
  aiFollowupsSchema,
  allCasesSchema,
  creditReportSchema,
  customTermsSchema,
  dataReferenceSchema,
  documentRequestUserSchema,
  documentRequestUserFileSchema,
  followupHistorySchema,
  formRequestSchema,
  invoiceSchema,
  previewDocumentSchema,
  formRequestTemplateSchema,
  workflowTemplateSchema,
  userWorkflowDocumentsSchema,
  helpCenterSchema,
  submissionAttemptSchema,
  pacerPreviewSchema,
  governmentFormSchema,
  bankStatementOrganizerSchema,
  ESignatureRequestSchema,
]);

export type QueryStateSchema = z.infer<typeof queryStateSchema>;

export const queryStateParser = parseAsArrayOf(parseAsJson(queryStateSchema.parse)).withOptions({ history: 'push' });

// Types that can be created with just id, key, and type (no additional required fields)
type SimpleQueryStateType = Exclude<QueryStateSchema['type'], 'anvilCastEditor'>;

export const backwardsCompatabilityFactory =
  ({ type, id }: { id: string; type: SimpleQueryStateType }) =>
  (state: QueryStateSchema[] | null): QueryStateSchema[] | null => {
    const hasThis = Boolean(state?.find((s) => s.type === type && s.id === id));
    if (!state || !hasThis) {
      const newItem = {
        id,
        key: nanoid(),
        type,
      };
      return [...(state || []), newItem];
    }

    return state;
  };
